'use client';

import type { Location} from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeftSmall, ArrowRightSmall, Close } from '@zoomit/icons';
import { Box, cn, Flex, IconButton, Image, Link } from '@zoomit/primitives';
import type { MenuItemType} from '@zoomit/web-kit';
import { BaseMediaUrl, IconRenderer, onMenuClick, OriginURL } from '@zoomit/web-kit';
import { cssVar, Hr, theme, Typography } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import MenuAvatarMobile from './DefaultMenuMobileAvatar';

type Props = {
  onCloseMenu(): void;
};

const DefaultMenuSubRouteMobile = (props: Props) => {
  const { onCloseMenu } = props;

  const navigate = useNavigate();

  const { state: subItem } = useLocation() as Location<MenuItemType>;

  const handleNavigate = (item: MenuItemType) => {
    navigate('/home');
    onCloseMenu();
    onMenuClick({ menu: item.title, device: 'mobile' });
  };

  const tenantLogoRenderer = useWebKitCustomization((x) => x.tenantLogoRenderer);

  return (
    <Box className={'h-screen overflow-hidden'}>
      <header
        className={
          'bg-background2 border-b-grey6 flex w-full items-center justify-between border-b px-4 py-[13px]'
        }
      >
        <IconButton onClick={onCloseMenu}>
          <Close fill={cssVar(theme.grey3)} size="xl" />
        </IconButton>

        <Link href={`${OriginURL}/product/`} style={{ display: 'flex' }}>
          {tenantLogoRenderer({ fill: cssVar(theme.text) })}
        </Link>

        <MenuAvatarMobile Logo={() => tenantLogoRenderer({ fill: cssVar(theme.text) })} />
      </header>

      <div className={'bg-grey7 sticky top-14 h-12 px-4 py-2.5'}>
        <IconButton
          className={
            'text-subhead text-grey1 absolute top-1/2 right-4 flex -translate-y-1/2 items-center gap-1 leading-6 font-normal'
          }
          onClick={() => navigate(-1)}
        >
          <ArrowRightSmall fill={cssVar(theme.grey3)} size="sm" />
          بازگشت
        </IconButton>
        <Typography variant="h6" lineHeight={2} textAlign="center">
          {subItem.title}
        </Typography>
      </div>

      <div className={'flex h-[calc(100vh-104px)] flex-col gap-2 overflow-auto px-6 py-4'}>
        {subItem.children?.map((item, index) => (
          <>
            {canDisplayMenuItem.renderImage(item.displayType) && item.image && (
              <div className={'aspect-primary relative w-full shrink-0 overflow-hidden rounded-md'}>
                <Image
                  src={`${BaseMediaUrl}/${item.image.id}`}
                  alt={item.image.alt}
                  fill
                  className={'object-cover'}
                />
              </div>
            )}
            {item.children && item.children.length > 0 ? (
              <Flex
                key={`${index}-${item.title}`}
                alignItems="center"
                justifyContent="space-between"
                onClick={() => navigate(`/submenu/${item.title}`, { state: item })}
              >
                <div
                  className={cn(
                    'text-body text-text before:bg-primary1 [&_svg]:fill-grey1 flex items-center gap-2 leading-7 before:h-4 before:w-1 before:content-[""] [&_svg]:h-3 [&_svg]:w-3',
                    item.isHighlighted ? 'font-black' : 'font-normal',
                    item.isHighlighted && !item.icon ? 'before:inline-block' : 'before:hidden',
                  )}
                >
                  {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                    <IconRenderer icon={item.icon.content} size={'sm'} />
                  )}
                  {item.title}
                </div>
                {item.children && item.children.length > 0 && <ArrowLeftSmall size="sm" />}
              </Flex>
            ) : (
              <Link
                key={`${index}-${item.title}-with-url`}
                href={item.url}
                rel={item.noFollow ? 'nofollow' : undefined}
                onClick={() => handleNavigate(item)}
                className={cn(
                  'text-body text-text before:bg-primary1 [&_svg]:fill-grey1 hover:text-primary1 hover:[&_svg]:fill-primary1 flex items-center gap-2 leading-7 before:h-4 before:w-1 before:content-[""] [&_svg]:h-3 [&_svg]:w-3',
                  item.isHighlighted ? 'font-black' : 'font-normal',
                  item.isHighlighted && !item.icon ? 'before:inline-block' : 'before:hidden',
                )}
              >
                {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                  <IconRenderer icon={item.icon.content} size={'sm'} />
                )}
                {item.title}
              </Link>
            )}
          </>
        ))}
        {subItem.indicatorButton && (
          <>
            <Hr />
            <Link
              className={cn(
                'text-body text-text before:bg-primary1 [&_svg]:fill-grey1 hover:text-primary1 hover:[&_svg]:fill-primary1 flex items-center gap-2 leading-7 before:h-4 before:w-1 before:content-[""] [&_svg]:h-3 [&_svg]:w-3',
                subItem.indicatorButton.icon ? 'before:inline-block' : 'before:hidden',
              )}
              href={subItem.indicatorButton.url}
            >
              {subItem.indicatorButton.icon && <IconRenderer icon={subItem.indicatorButton.icon.content} />}
              {subItem.indicatorButton.title}
            </Link>
          </>
        )}
      </div>
    </Box>
  );
};

export default DefaultMenuSubRouteMobile;
